import OrderEditor from "./OrderEditor";
import CustomDrawer from "@/components/drawer";
import Header from "@/components/header";
import ListItemLoading from "@/components/loading/listItemLoading/listItemLoading";
import { NoResult } from "@/components/noResult";
import useIsSmall from "@/hooks/useIsSmall";
import { MerchantServiceCategoryDto, MerchantServiceDto } from "@/models/club";
import SearchBox from "@/modules/schedule/searchBox";
import ServiceItem from "@/modules/schedule/serviceItem";

import { useGetMerchantServices } from "@/services/appointment/hooks/useGetMerchantServices";
import {
  ActionIcon,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  GridCol,
  Group,
  Menu,
  MenuItem,
  ScrollArea,
  Stack,
  Text,
  Title
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconDotsVertical, IconPlus } from "@tabler/icons-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const SCHEDULE_SERVICES_CATEGORY_KEY = "schedule-services-category";

function ScheduleServices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSmall = useIsSmall();
  const [search, setSearch] = useState<string>("");
  const { data, isLoading } = useGetMerchantServices();
  const active =
    searchParams.get("category") ??
    (typeof window !== "undefined"
      ? localStorage.getItem(SCHEDULE_SERVICES_CATEGORY_KEY) ?? "all"
      : "all");
  const setActive = useCallback(
    (value: string) => {
      const next = value === "all" ? "all" : value;
      if (typeof window !== "undefined") {
        if (next === "all") {
          localStorage.removeItem(SCHEDULE_SERVICES_CATEGORY_KEY);
        } else {
          localStorage.setItem(SCHEDULE_SERVICES_CATEGORY_KEY, next);
        }
      }
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          if (next === "all") {
            nextParams.delete("category");
          } else {
            nextParams.set("category", next);
          }
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [openedEdit, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);

  const [selectedService, setSelectedService] = useState<
    | { merchantService: MerchantServiceDto; categoryId: number | undefined }
    | undefined
  >();

  const handleSearchChange = useCallback(
    (value: string) => {
      if (value.length >= 2) {
        setSearch(value);
      } else if (value.length === 0) {
        setSearch("");
      }
    },
    []
  );

  // Restore category from localStorage into URL when returning (e.g. after add)
  useEffect(() => {
    if (searchParams.get("category")) return;
    const saved = typeof window !== "undefined"
      ? localStorage.getItem(SCHEDULE_SERVICES_CATEGORY_KEY)
      : null;
    if (saved && saved !== "all") {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("category", saved);
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  // const handleActiveChange = useCallback(
  //   (value: string | string[] | null) => {
  //     const resolvedValue = Array.isArray(value)
  //       ? value[0] ?? "all"
  //       : value ?? "all";
  //     const nextValue = resolvedValue || "all";
  //     setActive(nextValue);
  //     applyFiltersToUrl(nextValue, search);
  //   },
  //   [applyFiltersToUrl, search]
  // );

  // useEffect(() => {
  //   const currentString = searchParams.toString();

  //   if (currentString === lastAppliedParamsRef.current) {
  //     return;
  //   }

  //   lastAppliedParamsRef.current = currentString;

  //   const categoryParam = searchParams.get("category");
  //   const nextActive = categoryParam ?? "all";
  //   setActive((prev) => (prev === nextActive ? prev : nextActive));

  //   const searchParam = searchParams.get("q") ?? "";
  //   setSearch((prev) => (prev === searchParam ? prev : searchParam));
  // }, [searchParams]);

  useEffect(() => {
    const handleScroll = () => {
      if (stickyRef.current) {
        const rect = stickyRef.current.getBoundingClientRect();
        // Check if the element is stuck (top position is at or below 56px - header height)
        setIsSticky(rect.top <= 56);
      }
    };

    // Try to find the scrollable container
    const scrollableContainer = document.querySelector(
      "#dashboard-layout"
    ) as HTMLElement;

    if (scrollableContainer) {
      scrollableContainer.addEventListener("scroll", handleScroll);
      return () =>
        scrollableContainer.removeEventListener("scroll", handleScroll);
    } else {
      // Fallback to window scroll
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const filterSelected =
    active === "all" ? data : data?.filter((x) => x.id === Number(active));
  const filteredData = filterSelected?.map((x) => ({
    ...x,
    services: x.services?.filter((y) => y?.title?.includes(search)),
  }));

  const handle = (category: MerchantServiceCategoryDto) => {
    navigate(`editCategoryService`, {
      state: {
        category: category,
      },
    });
  };

  return (
    <>
      {
        isSmall ? <Header
          title="خدمات"
          showBack
          customComponent={
            <Button
              px="xs"
              variant="default"
              leftSection={<IconPlus size={14} />}
              onClick={() => open()}
            >
              افزودن
            </Button>
          }
        /> : null
      }

      <Container w="100%" size="md" bg="white">
        <Group justify="space-between">
          <Stack gap="0" >
            <Title order={4}>خدمات</Title><OrderEditor />
            <Text size="sm" pt="xs">
              خدمات ارائه شده توسط کسب و کار خود را مشاهده و مدیریت کنید.{" "}
            </Text>
          </Stack>
          {
            !isSmall && <Menu>
              <Menu.Target>
                <Button
                  px="xs"
                  variant="default"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => open()}
                >
                  افزودن
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => navigate("addCategoryService")}>
                  <Text fw="500">افزودن دسته بندی</Text>
                </Menu.Item>
                <Menu.Item onClick={() => navigate(active !== "all" ? `add?catId=${active}` : "add")}>
                  <Text fw="500">افزودن خدمت </Text>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          }

        </Group>

        <Stack mt="md" justify="start" flex={1}>
          <Stack
            ref={stickyRef}
            gap="lg"
            pt="sm"
            styles={{
              root: {
                position: "sticky",
                top: "56px", // Header height to stick below the header
                zIndex: 15, // Higher than header but still allows proper stacking
                backgroundColor: "white",
                borderBottom: isSticky
                  ? "1px solid var(--mantine-color-gray-2)"
                  : "none",
                transition: "border-bottom 0.2s ease",
              },
            }}
          >
            <SearchBox setSearch={handleSearchChange} initialValue={search} />
          </Stack>
          {/* 
          <DirectionProvider initialDirection="rtl">
            <ScrollArea pb="md">
              <Group
                wrap="nowrap"
                gap="xs"
                style={{ alignItems: "flex-start" }}
              > */}

          <Grid>
            <GridCol span={isSmall ? 12 : 4} >
              {
                isSmall ? <Stack>
                  <Chip.Group
                    value={active}
                    onChange={(value) => setActive(value as string)}
                  >
                    <ScrollArea pb="md">
                      <Group wrap="nowrap">
                        <Chip

                          value="all"
                          styles={{
                            label: {
                              padding: "20px",
                            },
                            iconWrapper: {
                              display: "none",
                            },
                          }}
                        >
                          همه دسته بندی
                        </Chip>
                        {data?.map((category) => (
                          <Chip
                            styles={{
                              label: {
                                padding: "20px",
                              },
                              iconWrapper: {
                                display: "none",
                              },
                            }}
                            key={category?.id}
                            value={category?.id?.toString()}

                          >
                            <Group
                              gap="xs"
                              wrap="nowrap"
                              justify="center"
                              align="center"
                            >
                              <Text>{category?.title}</Text>

                              <Text
                                size="xs"
                                c="dark"
                                bg="gray.3"
                                styles={{
                                  root: {
                                    padding: "2px 8px",
                                    borderRadius: "100px",
                                  },
                                }}
                              >
                                {category?.services?.length}
                              </Text>
                            </Group>
                          </Chip>
                        ))}
                      </Group>
                    </ScrollArea>
                  </Chip.Group>
                </Stack> :
                  <Card withBorder>
                    <Menu>
                      <MenuItem onClick={() => setActive("all")} bg={active === "all" ? "var(--mantine-color-gray-3)" : ""}>
                        <Text>
                          همه دسته بندی
                        </Text>
                      </MenuItem>

                      {data?.map((category) => (
                        <MenuItem onClick={() => setActive(category?.id?.toString() || "all")} bg={active === category?.id?.toString() ? "var(--mantine-color-gray-3)" : ""}>
                          <Group justify="space-between">
                            <Text>{category?.title}</Text>
                            <Text
                              size="xs"
                              c="dark"
                              bg="gray.3"
                              styles={{
                                root: {
                                  padding: "2px 8px",
                                  borderRadius: "100px",
                                },
                              }}
                            >
                              {category?.services?.length}
                            </Text>
                          </Group>
                        </MenuItem>

                      ))}
                    </Menu>
                  </Card>
              }
            </GridCol >
            <GridCol span={isSmall ? 12 : 8}>
              {isLoading ? (
                <ListItemLoading count={4} />
              ) : (
                <>
                  {filteredData?.length === 0 && <NoResult />}
                  <Stack>
                    {filteredData?.map((category) => (
                      <React.Fragment key={category?.id}>
                        <Stack key={category?.id}>
                          <Group justify="space-between">
                            <Text fw="500" size="lg">
                              {category?.title}
                            </Text>
                            <Menu shadow="md" width={200}>
                              <Menu.Target>
                                {
                                  isSmall ? <ActionIcon variant="subtle" >
                                    <IconDotsVertical />
                                  </ActionIcon> :
                                    <Button variant="default" radius="xl" size="xs" rightSection={<IconChevronDown size={15} />}>
                                      <Text size="xs">
                                        علمیات
                                      </Text>
                                    </Button>
                                }
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item onClick={() => handle(category)}>
                                  ویرایش
                                </Menu.Item>
                                <Menu.Item
                                  onClick={() =>
                                    navigate(`add?catId=${category?.id}`)
                                  }
                                >
                                  افزودن خدمت
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Group>
                          {category?.services && category?.services?.length > 0 ? (
                            category?.services?.map((service, serviceIndex) => (
                              <Stack
                                onClick={() => {
                                  openEdit();
                                  setSelectedService({
                                    merchantService: service,
                                    categoryId: category?.id,
                                  });
                                }}
                              >
                                <ServiceItem
                                  catId={category?.id || 0}
                                  service={service}
                                  key={serviceIndex}
                                />
                              </Stack>
                            ))
                          ) : (
                            <Card
                              h="50"
                              withBorder
                              styles={{
                                root: {
                                  borderColor: "var(--mantine-color-gray-2)",
                                },
                              }}
                            >
                              <Group justify="center" align="center">
                                <Text size="xs" c="var(--mantine-color-gray-4)">
                                  هنوز سرویسی اضافه نشده است
                                </Text>
                              </Group>
                            </Card>
                          )}
                        </Stack>
                      </React.Fragment>
                    ))}
                  </Stack>
                </>
              )}
            </GridCol>
          </Grid >





        </Stack >
      </Container >
      <CustomDrawer
        opened={opened && isSmall}
        onClose={close}
        close={close}
        size="200"
        position="bottom"
        custom="menu-service"
        radius="md"
        title=""
      >
        <Stack gap="0">
          <Menu>
            <Menu.Item onClick={() => navigate("addCategoryService")}>
              <Text fw="500">افزودن دسته بندی</Text>
            </Menu.Item>
            <Menu.Item onClick={() => navigate(active !== "all" ? `add?catId=${active}` : "add")}>
              <Text fw="500">افزودن خدمت </Text>
            </Menu.Item>
          </Menu>
        </Stack>
      </CustomDrawer>

      <CustomDrawer
        opened={openedEdit && isSmall}
        onClose={closeEdit}
        close={closeEdit}
        size="200"
        position="bottom"
        custom="edit-service"
        radius="md"
        title=""
      >
        <Stack>
          <Menu>
            <Menu.Item
              onClick={() =>
                navigate(`editService`, {
                  state: {
                    service: selectedService?.merchantService,
                    categoryId: selectedService?.categoryId,
                  },
                })
              }
            >
              <Text fw="500">ویرایش خدمت</Text>
            </Menu.Item>
          </Menu>
        </Stack>
      </CustomDrawer>
    </>
  );
}

export default ScheduleServices;
